/* ═══════════════════════════════════════════════════════════════════════════
   StoreGroupDashboard.js — store_group modu için ayrı dashboard.
   Tüm orijinal özellikler korundu: şube seçici, loadBase/loadDetail,
   realtime, heatmap, weekdays, hourAnalysis, AlertsPanel.
   ═══════════════════════════════════════════════════════════════════════════ */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import api           from '../../api/client';
import HeatmapChart  from '../../components/HeatmapChart';
import AlertsPanel   from '../../components/AlertsPanel';

/* ── Yardımcılar ── */
const fmt = n => Number(n || 0).toLocaleString('tr-TR');

const DAY_NAMES = ['','Paz','Pzt','Sal','Çar','Per','Cum','Cmt'];

/* Tarih yardımcıları — UTC kayması yok */
const TZ       = 'Europe/Istanbul';
const todayStr = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
const daysAgo  = n => { const d = new Date(); d.setDate(d.getDate() - n); return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d); };
const safeDate = raw => {
  if (!raw) return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(raw);
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border2)', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4 }}>{label}</div>
      {payload.map(p => <div key={p.dataKey} style={{ color:p.color }}>{p.name}: <strong>{Number(p.value).toLocaleString('tr-TR')}</strong></div>)}
    </div>
  );
};

/* Mini metrik kartı */
const MiniCard = ({ label, value, icon, color = 'var(--accent)', sub, loading }) => (
  <div className="card" style={{ padding:'14px 16px' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
      <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)' }}>{label}</div>
      <span style={{ fontSize:17 }}>{icon}</span>
    </div>
    {loading
      ? <div style={{ height:30, background:'var(--border)', borderRadius:4, animation:'pulse 1.2s ease infinite' }} />
      : <div style={{ fontSize:24, fontFamily:'var(--font-display)', fontWeight:700, color, lineHeight:1 }}>{value}</div>}
    {sub && <div style={{ fontSize:11, color:'var(--text3)', marginTop:5 }}>{sub}</div>}
  </div>
);

/* Şube satırı */
const BranchRow = ({ branch, isTop, onClick, selected }) => {
  const online = branch.status === 'online';
  return (
    <div onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:12, padding:'11px 14px',
      background: selected ? 'var(--accent-dim)' : isTop ? 'rgba(34,197,94,0.08)' : 'var(--bg3)',
      border:`1px solid ${selected ? 'var(--accent)' : isTop ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
      borderRadius:'var(--r)', cursor:'pointer', transition:'all 0.15s', marginBottom:6,
    }}>
      <span style={{ width:9, height:9, borderRadius:'50%', flexShrink:0, background:online?'var(--green)':'var(--text3)', boxShadow:online?'0 0 5px var(--green)':'none' }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
          {branch.name}
          {isTop && <span style={{ fontSize:10, background:'var(--green)', color:'#fff', borderRadius:10, padding:'1px 7px', fontWeight:600 }}>En Yoğun</span>}
        </div>
        {branch.location && <div style={{ fontSize:11, color:'var(--text3)' }}>📍 {branch.location}</div>}
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontSize:20, fontFamily:'var(--font-display)', fontWeight:700, color:isTop?'var(--green)':'var(--text)' }}>{fmt(branch.today_in)}</div>
        <div style={{ fontSize:10, color:'var(--text3)' }}>giriş</div>
      </div>
      <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:12, background:online?'rgba(34,197,94,0.12)':'var(--bg3)', color:online?'var(--green)':'var(--text3)', flexShrink:0 }}>
        {online ? 'Aktif' : 'Pasif'}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ANA BİLEŞEN
   ═══════════════════════════════════════════════════════════════════════════ */
export default function StoreGroupDashboard() {
  const navigate = useNavigate();

  /* ── State — orijinal yapı korunuyor ── */
  const [branches,      setBranches]      = useState([]);
  const [selBranch,     setSelBranch]     = useState('');
  const [summ,          setSumm]          = useState(null);
  const [rt,            setRt]            = useState(null);
  const [dailyData,     setDailyData]     = useState([]);
  const [heatmap,       setHeatmap]       = useState([]);
  const [weekdays,      setWeekdays]      = useState([]);
  const [hourAnalysis,  setHourAnalysis]  = useState(null);
  const [alarms,        setAlarms]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const rtRef = useRef(null);

  const dParam = selBranch ? { device_id: selBranch } : {};

  /* loadBase — şube listesi + özet + alarmlar */
  const loadBase = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [br, s, al] = await Promise.all([
        api.get('/reports/devices-summary'),
        api.get('/reports/summary',  { params: dParam }),
        api.get('/reports/alerts'),
      ]);
      setBranches(br.data.data || []);
      setSumm(s.data.data);
      setAlarms(al.data.data?.alarms || []);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [selBranch]); // eslint-disable-line

  useEffect(() => { loadBase(); }, [loadBase]);

  /* loadDetail — tek şube grafikleri */
  const loadDetail = useCallback(async () => {
    if (!selBranch) { setDailyData([]); setHeatmap([]); setWeekdays([]); setHourAnalysis(null); return; }
    setDetailLoading(true);
    try {
      const p = { device_id: selBranch };
      const [d, hm, wd, ha] = await Promise.all([
        api.get('/reports/daily',         { params: { ...p, days:30  } }),
        api.get('/reports/heatmap',       { params: { ...p, days:14  } }),
        api.get('/reports/weekdays',      { params: { ...p, weeks:12 } }),
        api.get('/reports/hour-analysis', { params: { ...p, days:30  } }),
      ]);
      setDailyData((d.data.data || []).map(r => ({
        ...r,
        date:     String(r.date).split('T')[0],
        total_in: Number(r.total_in),
      })));
      setHeatmap(hm.data.data || []);
      setWeekdays(wd.data.data?.data || []);
      setHourAnalysis(ha.data.data);
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  }, [selBranch]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  /* Realtime 15 sn */
  const loadRt = useCallback(() => {
    Promise.all([
      api.get('/reports/realtime', { params: dParam }),
      api.get('/reports/hourly', { params: dParam })
    ]).then(([realtimeRes, hourlyRes]) => {
      setRt({
        ...(realtimeRes.data.data || {}),
        hourly: hourlyRes.data.data?.data || hourlyRes.data.data || []
      });
    }).catch(() => {});
  }, [selBranch]); // eslint-disable-line

  useEffect(() => {
    loadRt();
    rtRef.current = setInterval(loadRt, 15000);
    return () => clearInterval(rtRef.current);
  }, [loadRt]);

  /* 60 sn silent refresh */
  useEffect(() => {
    const iv = setInterval(() => loadBase(true), 60000);
    return () => clearInterval(iv);
  }, [loadBase]);

  /* Hesaplamalar */
  const activeBranches  = branches.filter(b => b.status === 'online');
  const passiveBranches = branches.filter(b => b.status !== 'online');
  const topBranch       = [...branches].sort((a, b) => Number(b.today_in) - Number(a.today_in))[0];
  const totalToday      = branches.reduce((s, b) => s + Number(b.today_in || 0), 0);
  const avgPerBranch    = activeBranches.length ? Math.round(totalToday / activeBranches.length) : 0;
  const selectedBranch  = branches.find(b => String(b.id) === String(selBranch));

  /* ════════════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════════════ */
  return (
    <div>
      {/* Başlık + şube seçici */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, marginBottom:3 }}>Şube Analitik</h1>
          <p style={{ fontSize:12, color:'var(--text3)' }}>
            <span style={{ color:'var(--green)', fontWeight:600 }}>{activeBranches.length} aktif</span>
            {passiveBranches.length > 0 && <span style={{ color:'var(--text3)', marginLeft:10, fontWeight:600 }}>{passiveBranches.length} pasif</span>}
            <span style={{ color:'var(--text3)', marginLeft:10 }}>• {branches.length} şube</span>
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <select value={selBranch} onChange={e => setSelBranch(e.target.value)} style={{ minWidth:200 }}>
            <option value="">Tüm Şubeler</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.status==='online'?'● ':'○ '}{b.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => loadBase()} title="Yenile">↻</button>
        </div>
      </div>

      {/* Alarmlar */}
      {alarms.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <AlertsPanel alarms={alarms} compact />
        </div>
      )}

      {/* Üst özet kartlar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))', gap:12, marginBottom:22 }}>
        <MiniCard label="Toplam Bugün" value={fmt(selBranch ? summ?.today : totalToday)} icon="👥" color="var(--accent)"
          sub={selBranch ? (selectedBranch?.name||'') : `${branches.length} şube toplamı`} loading={loading} />
        <MiniCard label={selBranch ? 'Şube Durumu' : 'Aktif Şube'} value={selBranch ? (selectedBranch?.status === 'online' ? 'Aktif' : 'Pasif') : String(activeBranches.length)} icon="🏪" color="var(--green)"
          sub={selBranch ? (selectedBranch?.name || 'Tek şube görünümü') : `${branches.length} şubeden`} loading={loading} />
        <MiniCard label={selBranch ? 'Seçili Şube' : 'En Yoğun Şube'} value={selBranch ? (selectedBranch?.name||'—') : (topBranch?.name||'—')} icon="🏆" color="var(--yellow)"
          sub={fmt(selBranch ? summ?.today : topBranch?.today_in) + ' ziyaret'} loading={loading} />
        <MiniCard label={selBranch ? 'Bugünkü Giriş' : 'Şube Başına Ort.'} value={selBranch ? fmt(summ?.today) : fmt(avgPerBranch)} icon="📊" color="var(--purple)"
          sub={selBranch ? 'Seçili şube' : 'Bugün'} loading={loading} />
      </div>

      {/* ═══════════════════════════════════
          TÜM ŞUBELER görünümü
          ═══════════════════════════════════ */}
      {!selBranch && (
        <>
          {/* İki kolon layout */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:16, alignItems:'start' }}>

            {/* Sol: şube sıralaması + bar grafik */}
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <h2 style={{ fontSize:15, fontWeight:500 }}>Şube Performansı</h2>
                <span style={{ fontSize:12, color:'var(--text3)' }}>Bugünkü ziyaretçi sayıları</span>
              </div>

              {branches.length > 0 && (
                <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
                  <ResponsiveContainer width="100%" height={Math.max(160, branches.length * 38)}>
                    <BarChart
                      data={[...branches].sort((a,b) => Number(b.today_in) - Number(a.today_in))}
                      layout="vertical"
                      margin={{ top:4, right:20, bottom:4, left:4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" tick={{ fill:'var(--text3)', fontSize:10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill:'var(--text2)', fontSize:11 }} width={110} />
                      <Tooltip content={<Tip />} />
                      <Bar dataKey="today_in" name="Bugün Ziyaret" radius={[0,4,4,0]}>
                        {[...branches].sort((a,b) => Number(b.today_in) - Number(a.today_in)).map((b, i) => (
                          <rect key={b.id} fill={b.id===topBranch?.id ? 'var(--green)' : 'var(--accent)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {[...branches].sort((a,b) => Number(b.today_in) - Number(a.today_in)).map(b => (
                <BranchRow key={b.id} branch={b} isTop={b.id===topBranch?.id} selected={false}
                  onClick={() => setSelBranch(String(b.id))} />
              ))}
            </div>

            {/* Sağ: anlık + karşılaştırma + hızlı erişim */}
            <div style={{ display:'flex', flexDirection:'column', gap:14, position:'sticky', top:16 }}>

              {/* Realtime mini kartlar */}
              {rt && (
                <div className="card" style={{ padding:'14px 16px' }}>
                  <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:10 }}>Anlık (tüm şubeler)</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {[
                      { l:'İçerideki', v:rt.inside_count, c:'var(--accent)' },
                      { l:'Son 15 dk', v:rt.last_15min,   c:'var(--purple)' },
                      { l:'Bugün',     v:rt.today_in,     c:'var(--green)'  },
                      { l:'Son 1 sa',  v:rt.last_60min,   c:'var(--yellow)' },
                    ].map(x => (
                      <div key={x.l}>
                        <div style={{ fontSize:10, color:'var(--text3)' }}>{x.l}</div>
                        <div style={{ fontSize:18, fontWeight:700, color:x.c }}>{fmt(x.v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Saatlik BarChart — genel */}
              <div className="card" style={{ padding:'14px 16px' }}>
                <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:10 }}>Bugünkü Saatlik Dağılım</div>
                {(() => {
                  const hourlyRows = rt?.hourly_distribution || rt?.hourly?.data || rt?.hourly || [];
                  const chartRows = hourlyRows.map(r => ({
                    hour: r.hour,
                    count: Number(r.count ?? r.total_in ?? 0)
                  }));
                  return chartRows.length ? (
                    <ResponsiveContainer width="100%" height={90}>
                      <BarChart data={chartRows} margin={{ top:2, right:4, bottom:2, left:-24 }}>
                        <XAxis dataKey="hour" tick={{ fill:'var(--text3)', fontSize:8 }} tickFormatter={h=>`${h}:00`} />
                        <Tooltip
                          cursor={{ fill:'rgba(255,255,255,.04)' }}
                          contentStyle={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)' }}
                          labelFormatter={(h) => `${h}:00`}
                          formatter={(v) => [`${Number(v || 0).toLocaleString('tr-TR')} giriş`, 'Giriş']}
                        />
                        <Bar dataKey="count" fill="var(--accent)" radius={[2,2,0,0]} name="Giriş" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ fontSize:11, color:'var(--text3)', padding:8 }}>Saatlik veri yok</div>;
                })()}
              </div>

              {/* Hızlı erişim */}
              <div className="card" style={{ padding:'14px 16px' }}>
                <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:10 }}>Hızlı Erişim</div>
                {[
                  { icon:'ti-scale',          label:'Şubeler arası karşılaştır', path:'/dashboard/reports?module=cmpdev'  },
                  { icon:'ti-calendar-stats', label:'Tarih aralığı raporu',       path:'/dashboard/reports?module=range'   },
                  { icon:'ti-bell',           label:'Alarmları gör',               path:'/dashboard/alarms'                 },
                  { icon:'ti-download',       label:'Excel / PDF indir',            path:'/dashboard/reports?module=export'  },
                ].map(item => (
                  <button key={item.label} onClick={() => navigate(item.path)} className="quick-btn">
                    <i className={`ti ${item.icon}`} style={{ fontSize:14, color:'var(--accent2)' }} aria-hidden="true" />
                    {item.label}
                    <i className="ti ti-arrow-right" style={{ marginLeft:'auto', fontSize:12, opacity:.5 }} aria-hidden="true" />
                  </button>
                ))}
              </div>

            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════
          TEK ŞUBE görünümü
          ═══════════════════════════════════ */}
      {selBranch && (
        <>
          {/* Geri butonu + şube adı */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelBranch('')}>← Tüm Şubeler</button>
            {selectedBranch && (
              <div>
                <strong style={{ fontSize:15 }}>{selectedBranch.name}</strong>
                {selectedBranch.location && <span style={{ fontSize:12, color:'var(--text3)', marginLeft:8 }}>📍 {selectedBranch.location}</span>}
              </div>
            )}
          </div>

          {/* Anlık 4 kart */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12, marginBottom:22 }}>
            <MiniCard label="Bugün Giriş"  value={fmt(rt?.today_in)}   icon="🚶" color="var(--accent)"  loading={!rt} sub="Toplam" />
            <MiniCard label="Son 5 Dakika" value={fmt(rt?.last_5min)}  icon="⚡" color="var(--green)"  loading={!rt} />
            <MiniCard label="Son 15 Dakika"value={fmt(rt?.last_15min)} icon="🕒" color="var(--purple)" loading={!rt} />
            <MiniCard label="Son 1 Saat"   value={fmt(rt?.last_60min)} icon="⏱️" color="var(--yellow)" loading={!rt} />
          </div>

          {/* Son 30 Gün Trendi */}
          <div className="card" style={{ padding:'16px 18px', marginBottom:20 }}>
            <h3 style={{ fontSize:14, marginBottom:14 }}>Son 30 Gün Trendi</h3>
            {detailLoading ? (
              <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)' }}>
                <span className="spinner" style={{ marginRight:8 }} />Yükleniyor...
              </div>
            ) : dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={dailyData} margin={{ top:0, right:8, bottom:0, left:-18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill:'var(--text3)', fontSize:9 }} tickFormatter={v=>v?.slice(5)} />
                  <YAxis tick={{ fill:'var(--text3)', fontSize:10 }} />
                  <Tooltip content={<Tip />} />
                  <Line type="monotone" dataKey="total_in" stroke="var(--accent)" strokeWidth={2.5} dot={false} name="Ziyaret" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height:80, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)', fontSize:13 }}>
                Bu şube için veri bulunamadı
              </div>
            )}
          </div>

          {/* Isı haritası */}
          <div className="card" style={{ padding:'16px 18px', marginBottom:20 }}>
            <h3 style={{ fontSize:14, marginBottom:14 }}>Son 14 Gün Saat Yoğunluk Haritası</h3>
            {detailLoading
              ? <div style={{ height:80, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)' }}><span className="spinner" /></div>
              : <HeatmapChart data={heatmap} />}
          </div>

          {/* Haftanın günleri + Saat analizi */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16 }}>
            <div className="card" style={{ padding:'16px 18px' }}>
              <h3 style={{ fontSize:14, marginBottom:14 }}>Haftanın Günleri</h3>
              {detailLoading ? (
                <div style={{ height:80, display:'flex', alignItems:'center', justifyContent:'center' }}><span className="spinner" /></div>
              ) : weekdays.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={[...weekdays].sort((a,b)=>a.dow-b.dow)} margin={{ top:0, right:5, bottom:0, left:-20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="dow" tick={{ fill:'var(--text3)', fontSize:10 }} tickFormatter={d=>DAY_NAMES[d]||d} />
                      <YAxis tick={{ fill:'var(--text3)', fontSize:10 }} />
                      <Tooltip content={<Tip />} labelFormatter={d=>DAY_NAMES[d]||d} />
                      <Bar dataKey="avg_in" fill="var(--purple)" radius={[3,3,0,0]} name="Ort. Ziyaret" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ marginTop:10 }}>
                    {[...weekdays].sort((a,b)=>b.avg_in-a.avg_in).slice(0,3).map((d,i)=>(
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                        <span style={{ color:'var(--text2)' }}>{i+1}. {DAY_NAMES[d.dow]}</span>
                        <strong>{fmt(d.avg_in)}</strong>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div style={{ color:'var(--text3)', fontSize:12 }}>Yeterli veri yok</div>}
            </div>

            <div className="card" style={{ padding:'16px 18px' }}>
              <h3 style={{ fontSize:14, marginBottom:14 }}>Saat Analizi</h3>
              {detailLoading ? (
                <div style={{ height:80, display:'flex', alignItems:'center', justifyContent:'center' }}><span className="spinner" /></div>
              ) : hourAnalysis ? (
                <>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>En Yoğun Saatler</div>
                    {(hourAnalysis.busiest||[]).slice(0,5).map((h,i)=>(
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                        <span style={{ color:'var(--text2)' }}>{i+1}. {String(h||'—')}</span>
                        <strong style={{ color:'var(--accent2)' }}>{fmt(hourAnalysis.data?.find(r=>r.hour===h)?.avg_in||0)}</strong>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>En Sakin Saatler</div>
                    {(hourAnalysis.quietest||[]).slice(0,3).map((h,i)=>(
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                        <span style={{ color:'var(--text2)' }}>{i+1}. {String(h||'—')}</span>
                        <strong style={{ color:'var(--text3)' }}>{fmt(hourAnalysis.data?.find(r=>r.hour===h)?.avg_in||0)}</strong>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div style={{ color:'var(--text3)', fontSize:12 }}>Yeterli veri yok</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
